import React, { useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import UserReviewTile from './UserReviewTile'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import RatingDistribution from 'components/Common/RatingDistribution'
import RatingBreakdownItem from 'components/Common/RatingBreakdownItem'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import useUserReviews from 'hooks/Reviews/useUserReviews'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Carousel from 'LuxKit/Carousel/Carousel'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const GraphSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${rem(20)};
`

const RatingBreakdownSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${rem(24)};
  column-gap: ${rem(32)};
  padding: ${rem(16)} ${rem(64)};
`

const RatingBreakdownSectionMobile = styled(Group)`
  padding: 0 ${rem(8)};
`

const MobileGraphSection = styled(VerticalSpacer)`
  padding: ${rem(24)} 0;
`

// TODO: Replace with real data from backend
const MOCK_RATING_DISTRIBUTIONS = [
  { id: 1, label: 'Exceptional', range: '9.0+', progress: 71, count: 71 },
  { id: 2, label: 'Good', range: '7.0-9,0', progress: 20, count: 20 },
  { id: 3, label: 'Okay', range: '5.0-7.0', progress: 6, count: 6 },
  { id: 4, label: 'Poor', range: '3.0-5.0', progress: 3, count: 3 },
  { id: 5, label: 'Terrible', range: '1.0-3.0', progress: 3, count: 3 },
]

// TODO: Replace with real data from backend
const MOCK_RATING_BREAKDOWNS = [
  { id: 1, score: 9.1, label: 'Value' },
  { id: 2, score: 9.0, label: 'Facilities' },
  { id: 3, score: 8.7, label: 'Staff' },
  { id: 4, score: 9.6, label: 'Location' },
]

const pageSize = 9

interface Props {
  property?: App.OfferProperty | App.BedbankProperty
  isLPP?: boolean
  experience?: App.ExperienceOffer
  tour?: App.Tours.TourV2Offer
  showReviewFiltersAndUserDetails?: boolean
  reviewsThreshold?: number
}

function UserReviewCarousel({
  property,
  experience,
  tour,
  showReviewFiltersAndUserDetails = true,
  reviewsThreshold,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null)

  const [reviews, fetchingReviews] = useUserReviews({
    propertyId: property?.id,
    experienceId: experience?.id,
    tourId: tour?.id,
    useExternalReviews: true,
    useHiddenForProperty: true,
    limit: pageSize,
  })

  const displayableRating = property?.rating ?? experience?.rating ?? tour?.rating
  const isGoogleReviews = displayableRating?.reviewsSource === 'google'

  const visibleReviews = reviews?.reviews ?? []
  const offerRatingDisplayable = isOfferRatingDisplayable(displayableRating, reviewsThreshold)

  return (
    <Group
      direction="vertical"
      gap={24}
      ref={rootRef}
      id="customer-reviews"
    >
      <LoadingIndicator floating visible={fetchingReviews} />
      {/* Graph Section - Desktop */}
      <CSSBreakpoint min="tablet">
        <GraphSection>
          {/* Review Section */}
          <VerticalSpacer gap={32}>
            {offerRatingDisplayable && <NumberRating
              variant="xxl"
              rating={displayableRating.score}
              total={displayableRating.reviewsTotal}
              ratingSubtitle={isGoogleReviews ? 'Reviews from Google' : undefined}
            />}
            {/* ToDo: wait backend to provide the data */}
            <BodyTextBlock
              weight="bold"
              variant="medium"
              startIcon={<LineThumbsUpIcon colour="highlight-secondary" />}
            >
              89% recommend this hotel
            </BodyTextBlock>
          </VerticalSpacer>
          {/* Rating Distribution */}
          <VerticalSpacer gap={0}>
            {MOCK_RATING_DISTRIBUTIONS.map(item => (
              <RatingDistribution
                key={item.id}
                label={item.label}
                range={item.range}
                progress={item.progress}
                count={item.count}
              />
            ))}
          </VerticalSpacer>
          {/* Rating Breakdown */}
          <RatingBreakdownSection>
            {MOCK_RATING_BREAKDOWNS.map(item => (
              <RatingBreakdownItem
                key={item.id}
                score={item.score}
                label={item.label}
              />
            ))}
          </RatingBreakdownSection>
        </GraphSection>
      </CSSBreakpoint>
      {/* Graph Section - Mobile */}
      <CSSBreakpoint max="mobile">
        <MobileGraphSection gap={24}>
          {/* Review Section */}
          {offerRatingDisplayable && <NumberRating
            variant="xl"
            rating={displayableRating.score}
            total={displayableRating.reviewsTotal}
            ratingSubtitle={isGoogleReviews ? 'Reviews from Google' : undefined}
          />}
          {/* Rating Distribution */}
          <VerticalSpacer gap={8}>
            {MOCK_RATING_DISTRIBUTIONS.map(item => (
              <RatingDistribution
                key={item.id}
                label={item.label}
                range={item.range}
                progress={item.progress}
                count={item.count}
              />
            ))}
          </VerticalSpacer>
          {/* Rating Breakdown */}
          <RatingBreakdownSectionMobile direction="horizontal" gap={0} horizontalAlign="space-between">
            {MOCK_RATING_BREAKDOWNS.map(item => (
              <RatingBreakdownItem
                key={item.id}
                score={item.score}
                label={item.label}
              />
            ))}
          </RatingBreakdownSectionMobile>
          {/* ToDo: wait backend to provide the data */}
          <BodyTextBlock
            weight="bold"
            variant="medium"
            startIcon={<LineThumbsUpIcon colour="highlight-secondary" />}
          >
            89% recommend this hotel
          </BodyTextBlock>
        </MobileGraphSection>
      </CSSBreakpoint>
      {/* Carousel Section - Desktop */}
      <CSSBreakpoint min="desktop">
        <VerticalSpacer gap={16}>
          <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
            <Heading variant="heading4">Reviews from verified guests</Heading>
            <TextButton kind="secondary">View all {offerRatingDisplayable ? displayableRating.reviewsTotal : ''} reviews</TextButton>
          </Group>
          <CardCarousel gap={20} pageSize={3}>
            {visibleReviews.map(review => <UserReviewTile
              key={review.id}
              userReview={{
                ...review,
                experienceId: experience?.id,
                tourId: tour?.id,
              }}
              variant="compact"
              showReviewFiltersAndUserDetails={showReviewFiltersAndUserDetails}
            />)}
          </CardCarousel>
        </VerticalSpacer>
      </CSSBreakpoint>
      {/* Carousel Section - Tablet */}
      <CSSBreakpoint max="tablet">
        <VerticalSpacer gap={16}>
          <Heading variant="heading4">Reviews from verified guests</Heading>
          <Carousel
            gap={20}
            width="80vw"
            tabletWidth="40vw"
            snap="start"
            gutterStyle="overflow"
          >
            {visibleReviews.map(review => <UserReviewTile
              key={review.id}
              userReview={{
                ...review,
                experienceId: experience?.id,
                tourId: tour?.id,
              }}
              variant="compact"
              showReviewFiltersAndUserDetails={showReviewFiltersAndUserDetails}
            />)}
          </Carousel>
          <Group direction="horizontal" horizontalAlign="center">
            <TextButton kind="secondary">View all {offerRatingDisplayable ? displayableRating.reviewsTotal : ''} reviews</TextButton>
          </Group>
        </VerticalSpacer>
      </CSSBreakpoint>
    </Group>
  )
}

export default React.memo(UserReviewCarousel)
